<template>
  <div>
    <div class="d-flex justify-content-between p-3 flex-wrap">
      <h3 class="flex-none">Assigned Locations</h3>
      <div class="flex-none">
        <!-- <b-button class="btn" @click="collapse = !collapse" variant="secondary">
          <font-awesome-icon icon="info" class="mr-2" />
          Learn More
        </b-button> -->
      </div>

      <div class="flex-none">
        <button
          class="btn btn-primary"
          @click="
            () => {
              this.$bvModal.show('assign-locations-modal');
            }
          "
          title="Add Member Assigned Locations"
        >
          <i class="fa fa-plus text-white"></i>
          <i class="fa fa-user text-white"></i>
          Add Member Assigned Locations
        </button>
      </div>
    </div>

    <!-- Information accordion -->
    <div class="bg-light border-top border-bottom p-3" :v-model="false" id="">
      “Assigned Locations” allows you to assign specific locations to members in
      your project. Members can then select from these assigned locations when
      making observations using datasheets set to 'Predefined list of locations'
      or 'Both'. There are two types of assigned locations:
      <ul>
        <li>
          <b><i class="fa fa-user"></i> Member - </b>You can assign locations to
          individual members. This can either be for all datasheets in the
          <b>project</b>, or for specific <b>datasheets</b>.
        </li>
        <li>
          <b><i class="fa fa-file-alt"></i> Datasheet - </b> You can assign
          locations to all project members for a specific datasheet. This can be
          done on the datasheet locations panel.
        </li>
      </ul>
      You can also enable "auto-assignment" of locations within the datasheet
      settings. With "auto-assignment,” when your participants create new
      observations, the locations of those observations will automatically be
      assigned to that person for all datasheets. This is useful when you want
      participants to create their own monitoring locations (example, their
      backyards) and then do repeat monitoring at that location.
    </div>

    <div class="card-body">
      <div class="row mb-3">
        <div class="col-lg-8 col-sm-12 col-md-10">
          <div class="row mb-3 justify-content-between">
            <div class="input-group col-12">
              <b-input-group-prepend>
                <span
                  class="btn btn-secondary"
                  title="Search assigned location's by"
                >
                  <i v-if="!isBusy" class="fas fa-search"></i>
                  <i v-if="isBusy" class="fas fa-spinner fa-spin"></i>
                </span>
              </b-input-group-prepend>
              <b-input-group-prepend class="col-3 m-0 p-0">
                <b-form-select
                  v-model="filterBy"
                  :options="searchFields"
                ></b-form-select>
              </b-input-group-prepend>
              <b-form-input
                class="form-control"
                :placeholder="searchPlaceholder"
                name="search"
                type="search"
                debounce="1000"
                v-model="searchText"
              ></b-form-input>
              <div class="input-group-append" v-if="searchText">
                <b-button @click="searchText = ''"
                  ><i class="fas fa-times-circle"></i
                ></b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p v-if="isSaving">
        Saving new assigned locations...
        <i class="fas fa-spinner fa-spin"></i>
      </p>
      <div class="row justify-content-between">
        <b-col md="3" class="my-1">
          Showing {{ fromItem }} to {{ toItem }} of {{ totalItems }} assigned
          location<span v-if="totalItems === 0 || totalItems > 1">s</span>
        </b-col>

        <b-col sm="2" md="3" class="my-1">
          <b-pagination
            :total-rows="totalItems"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0 pagination-holder"
            prev-text="Prev"
            next-text="Next"
            align="fill"
          />
        </b-col>

        <b-col sm="2" md="3" class="my-1">
          <b-form-group
            label="Per Page"
            label-align="right"
            label-cols-sm="7"
            label-cols-md="8"
            label-for="perPageSelect"
            class="mb-0"
          >
            <b-form-select
              v-model="perPage"
              id="perPageSelect"
              size="sm"
              :options="pageOptions"
            >
            </b-form-select>
          </b-form-group>
        </b-col>
      </div>
    </div>

    <b-table
      show-empty
      stacked="md"
      id="assignedLocationsManageTable"
      ref="assignedLocationsManageTable"
      :busy="isBusy"
      :items="assignments"
      :fields="tableFields"
      :filter="filterVal"
      :current-page="currentPage"
      :per-page="perPage"
      sort-icon-left
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      :empty-text="emptyText"
    >
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Assigned Locations..." />
      </template>
      <template #cell(type)="row">
        <template v-if="row.item.member"
          ><i class="fas fa-user mr-2"></i>Member</template
        >
        <template v-else-if="row.item.datasheet"
          ><i class="fas fa-file mr-2"></i>Datasheet</template
        >
        <template v-else><i class="fas fa-people mr-2"></i>Project</template>
      </template>
      <template #cell(location.name)="row">
        <b-link
          :to="{
            name: 'LocationAssignments',
            params: { id: row.item.location.id },
          }"
        >
          {{ row.item.location.name }}
        </b-link>
      </template>
      <template #cell(member.member.firstName)="row">
        <template v-if="row.item.member">
          {{ row.item.member.member.firstName }}
          {{ row.item.member.member.lastName }}
        </template>
        <template v-else>
          <i>All Members</i>
        </template>
      </template>
      <template #cell(datasheet.name)="row">
        <template v-if="row.item.datasheet">
          {{ row.item.datasheet.name }}
        </template>
        <template v-else>
          <i>All Datasheets</i>
        </template>
      </template>
      <template v-slot:cell(createdAt)="row">
        <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
      </template>
      <template v-slot:cell(updatedAt)="row">
        <DateTime :date="row.item.updatedAt" :dateTime="false"></DateTime>
      </template>
      <template v-slot:cell(options)="row">
        <div class="options-holder float-right">
          <b-btn
            size="sm"
            class="my-1 mr-1"
            @click="deleteAssignment(row.item)"
            alt="Remove this Assigned Location"
            title="Remove this Assigned Location"
          >
            <font-awesome-icon icon="times" />
            Delete
          </b-btn>
        </div>
      </template>
    </b-table>

    <AssignLocationsModal
      @on-add-assignments="onAddAssignments"
    ></AssignLocationsModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import VueLoader from "../../../layout/Loader.vue";
import RequiresMember from "../tabpermission/requiresMember.vue";
import AssignLocationsModal from "./assignLocationsModal.vue";
import DatasheetSelection from "../zooniverse/datasheetSelection.vue";
import ZooniverseUsername from "../zooniverse/username.vue";
import ZooniverseProject from "../zooniverse/project.vue";
import DateTime from "../../../../components/common/date-time/DateTime.vue";

export default {
  components: {
    ZooniverseProject,
    ZooniverseUsername,
    DatasheetSelection,
    AssignLocationsModal,
    RequiresMember,
    VueLoader,
    DateTime,
  },
  data() {
    return {
      pageOptions: [25, 50, 100, 250, 500],
      isBusy: false,
      isSaving: false,
      searchFields: [
        { value: "member.name", text: "Member Name" },
        { value: "datasheet.name", text: "Datasheet Name" },
        { value: "location.name", text: "Location Name" },
      ],
      tableFields: [
        {
          key: "location.name",
          label: "Location",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "type",
          label: "Assignment Type",
          sortable: false,
          sortDirection: "asc",
        },
        {
          key: "member.member.firstName",
          label: "Who",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "datasheet.name",
          label: "Datasheet",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "createdAt",
          label: "Created",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "updatedAt",
          label: "Updated",
          sortable: true,
          sortDirection: "desc",
        },
        { key: "options", label: "", sortable: false, class: "text-right" },
      ],
      emptyText: "No Assigned Locations Yet",
      searchText: "",
      filterBy: "member.name",
      currentPage: 1,
      perPage: 25,
      totalItems: 0,
      sortBy: "updatedAt",
      sortDesc: true,
      sortDirection: "desc",
    };
  },

  created() {
    if (this.projectVuex) {
      if (this.$refs.assignedLocationsManageTable)
        this.$refs.assignedLocationsManageTable.refresh();
    }
  },
  watch: {
    projectVuex(newVal) {
      if (newVal) {
        if (this.$refs.assignedLocationsManageTable)
          this.$refs.assignedLocationsManageTable.refresh();
      }
    },
    filterBy(newVal) {
      if (newVal) {
        this.searchText = "";
      }
    },
  },
  destroyed() {},
  computed: {
    ...mapGetters({
      projectVuex: "project/get/project",
      isPublicOrMember: "project/get/isPublicOrMember",
      membershipMap: "user/profile/membershipMap",
      isLoading: "project/assigned_locations/isLoading",
    }),
    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },
    searchPlaceholder() {
      let placeholder = "Search assigned location's by ";
      if (this.searchFields && this.filterBy) {
        placeholder =
          placeholder +
          this.searchFields.filter((val) => {
            return val.value === this.filterBy;
          })[0].text;
      }
      return placeholder;
    },
    filterVal: {
      get: function () {
        // This is our multi-filter computed value
        // - search by user first & last name, location name and datasheet name

        let retVal = "";
        if (this.searchText.length > 0) {
          switch (this.filterBy) {
            case "member.name":
              retVal =
                retVal.length === 0
                  ? "searchMember=" + this.searchText
                  : retVal + "&searchMember=" + this.searchText;
              break;
            case "location.name":
              retVal =
                retVal.length === 0
                  ? "location.name=" + this.searchText
                  : retVal + "&location.name=" + this.searchText;
              break;
            case "datasheet.name":
              retVal =
                retVal.length === 0
                  ? "datasheet.name=" + this.searchText
                  : retVal + "&datasheet.name=" + this.searchText;
              break;
          }
          this.hideReplies = false;
        }
        return retVal;
      },
    },
  },
  methods: {
    ...mapActions({
      getAssignedLocations: "project/assigned_locations/getManager",
      createAssignedLocation: "project/assigned_locations/create",
      deleteAssignedLocation: "project/assigned_locations/delete",
    }),

    assignments(ctx) {
      // Busy state is set
      this.isBusy = true;

      // URL Params are set
      let urlParams = this.urlBuilder(ctx);

      // Assigned locations are fetched
      return this.getAssignedLocations(urlParams).then((data) => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;
        return data["hydra:member"].map((assignments) => {
          return {
            ...assignments,
          };
        });
      });
    },

    // Handle return from create modal
    onAddAssignments(data) {
      console.log("save assigned locations", data);

      // Hide the modal manually
      this.$nextTick(() => {
        this.$bvModal.hide("assign-locations-modal");
      });
      this.isSaving = true;

      let promises = [];
      data.forEach((val) => {
        let promise = this.createAssignedLocation(val);
        promises.push(promise);
      });

      // wait till all requests are complete before reloading table
      Promise.all(promises).then((values) => {
        console.log("all assignments added", values);
        this.isSaving = false;
        this.$refs.assignedLocationsManageTable.refresh();
      });
    },

    deleteAssignment(item) {
      this.deleteAssignedLocation(item["@id"]).then((val) => {
        if (val) this.$refs.assignedLocationsManageTable.refresh();
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
